import type { ChangeEvent, ComponentPropsWithoutRef, JSX } from 'react';
import { forwardRef } from 'react';
import { RadioCardRoot } from './RadioCardRoot';
import { Image } from '../Image';
import type { Spacings } from '../../css/designTokens/spacing';

export type RadioCardImageProps = Omit<ComponentPropsWithoutRef<'input'>, 'onChange'> & {
  /**
   * Indicates the state of the radio
   */
  checked?: boolean;
  /**
   * Disables the card
   */
  disabled?: boolean;
  /**
   * Provides an ID for the input
   */
  id?: string;
  /**
   * The name attribute of the input
   */
  name?: string;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * The value attribute of the input
   */
  value: string;
  /**
   * The label text for the radio card
   */
  label: string;
  /**
   * The source URL for the image to display
   */
  imageSrc: string;
  /**
   * The aspect ratio of the card
   */
  aspectRatio?: string;

  /**
   * The padding of the card
   */
  padding?: Spacings;
};

export const RadioCardImage = forwardRef<HTMLInputElement, RadioCardImageProps>(
  ({ label, imageSrc, aspectRatio, padding = 'space-04', ...rootProps }, ref): JSX.Element => {
    return (
      <RadioCardRoot
        ref={ref}
        {...rootProps}
        aspectRatio={aspectRatio}
        padding={padding}
      >
        <Image
          alt={label}
          data-wui-radio-card-image={padding === 'space-00' ? 'cover' : 'contain'}
          fill={true}
          fit={padding === 'space-00' ? 'cover' : 'contain'}
          src={imageSrc}
        />
      </RadioCardRoot>
    );
  },
);

RadioCardImage.displayName = 'RadioCardImage';
